import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from '../axios';
import { RootState } from '../store';
import { useToast } from '../components/ToastProvider';
import StatusBadge from '../components/StatusBadge';
import MobileDataCard from '../components/MobileDataCard';

interface ManagedUser {
  id: string;
  email: string;
  role: 'ADMIN' | 'PARTNER' | 'CLOSER';
  status: 'ACTIVE' | 'BLOCKED';
  closerType?: 'TEAM_MEMBER' | 'EXTERNAL' | null;
  salesCompensationModel?: 'FIXED_1200' | 'FIXED_500' | null;
}

interface CommissionSettings {
  setterPercent: number;
  closerPercent: number;
  renewalPercent: number;
  fixed1200CombinedPercent: number;
  fixed500CombinedOriginatedPercent: number;
  fixed500CombinedInheritedPercent: number;
  fixed1200OffsetStartCommission: number;
  fixed1200OffsetFullCommission: number;
  fixed1200MonthlyFixumAmount: number;
  fixed500OffsetStartCommission: number;
  fixed500OffsetFullCommission: number;
  fixed500MonthlyFixumAmount: number;
}

const COMMISSION_FIELDS: Array<[keyof CommissionSettings, string]> = [
  ['setterPercent', 'userManagement.setterPercent'],
  ['closerPercent', 'userManagement.closerPercent'],
  ['renewalPercent', 'userManagement.renewalPercent'],
  ['fixed1200CombinedPercent', 'userManagement.fixed1200CombinedPercent'],
  ['fixed500CombinedOriginatedPercent', 'userManagement.fixed500CombinedOriginatedPercent'],
  ['fixed500CombinedInheritedPercent', 'userManagement.fixed500CombinedInheritedPercent'],
  ['fixed1200OffsetStartCommission', 'userManagement.fixed1200OffsetStartCommission'],
  ['fixed1200OffsetFullCommission', 'userManagement.fixed1200OffsetFullCommission'],
  ['fixed1200MonthlyFixumAmount', 'userManagement.fixed1200MonthlyFixumAmount'],
  ['fixed500OffsetStartCommission', 'userManagement.fixed500OffsetStartCommission'],
  ['fixed500OffsetFullCommission', 'userManagement.fixed500OffsetFullCommission'],
  ['fixed500MonthlyFixumAmount', 'userManagement.fixed500MonthlyFixumAmount'],
];

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings | null>(null);
  const [email, setEmail] = useState('');
  const [closerType, setCloserType] = useState<'TEAM_MEMBER' | 'EXTERNAL'>('TEAM_MEMBER');
  const [salesCompensationModel, setSalesCompensationModel] = useState<'FIXED_1200' | 'FIXED_500'>('FIXED_1200');

  const fetchUsers = async () => {
    const response = await axios.get('/api/admin/users');
    setUsers(response.data);
  };

  const fetchCommissionSettings = async () => {
    const response = await axios.get('/api/admin/commission-settings');
    setCommissionSettings(response.data);
  };

  const refreshAll = async () => {
    await Promise.all([fetchUsers(), fetchCommissionSettings()]);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const updateStatus = async (managedUser: ManagedUser, status: 'ACTIVE' | 'BLOCKED') => {
    const endpoint = managedUser.role === 'CLOSER'
      ? `/api/admin/closers/${managedUser.id}/status`
      : `/api/admin/partners/${managedUser.id}/status`;
    const successKey = status === 'BLOCKED' ? 'userManagement.blocked' : 'userManagement.unblocked';

    try {
      setBusy(`${managedUser.id}:STATUS:${status}`);
      await axios.patch(endpoint, { status });
      toast.show(t(successKey) as string, 'success');
      await fetchUsers();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.statusError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const updateRole = async (
    managedUser: ManagedUser,
    role: 'ADMIN' | 'PARTNER' | 'CLOSER',
    nextCloserType?: 'TEAM_MEMBER' | 'EXTERNAL',
    nextModel?: 'FIXED_1200' | 'FIXED_500' | null,
  ) => {
    try {
      setBusy(`${managedUser.id}:ROLE:${role}:${nextCloserType || 'NONE'}`);
      await axios.patch(`/api/admin/users/${managedUser.id}/role`, {
        role,
        closerType: role === 'CLOSER' ? (nextCloserType || 'TEAM_MEMBER') : null,
        salesCompensationModel: role === 'CLOSER' ? (nextCloserType === 'EXTERNAL' ? null : (nextModel || 'FIXED_1200')) : null,
      });
      const successKey = role === 'ADMIN'
        ? 'userManagement.promotedAdmin'
        : role === 'PARTNER'
          ? 'userManagement.demotedPartner'
          : nextCloserType === 'EXTERNAL'
            ? 'userManagement.promotedExternal'
            : 'userManagement.promotedTeam';
      toast.show(t(successKey) as string, 'success');
      await fetchUsers();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.roleError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const updateCloserType = async (managedUser: ManagedUser, nextType: 'TEAM_MEMBER' | 'EXTERNAL') => {
    try {
      setBusy(`${managedUser.id}:TYPE:${nextType}`);
      await axios.patch(`/api/admin/closers/${managedUser.id}/type`, { closerType: nextType });
      toast.show(t('userManagement.typeUpdated') as string, 'success');
      await fetchUsers();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.typeError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const updateCompensationModel = async (managedUser: ManagedUser, nextModel: 'FIXED_1200' | 'FIXED_500') => {
    try {
      setBusy(`${managedUser.id}:MODEL:${nextModel}`);
      await axios.patch(`/api/admin/users/${managedUser.id}/role`, {
        role: 'CLOSER',
        closerType: managedUser.closerType || 'TEAM_MEMBER',
        salesCompensationModel: nextModel,
      });
      toast.show(t('userManagement.modelUpdated') as string, 'success');
      await fetchUsers();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.modelError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async (managedUser: ManagedUser) => {
    try {
      setBusy(`${managedUser.id}:RESET`);
      await axios.post(`/api/admin/users/${managedUser.id}/reset-password`);
      toast.show(t('userManagement.passwordResetSent') as string, 'success');
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.resetError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const createCloser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setBusy('create');
      await axios.post('/api/admin/users', {
        email,
        role: 'CLOSER',
        status: 'ACTIVE',
        closerType,
        salesCompensationModel: closerType === 'TEAM_MEMBER' ? salesCompensationModel : null,
      });
      toast.show(t('userManagement.inviteSent') as string, 'success');
      setEmail('');
      setCloserType('TEAM_MEMBER');
      setSalesCompensationModel('FIXED_1200');
      await fetchUsers();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.createError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const saveCommissionSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commissionSettings) {
      return;
    }

    try {
      setBusy('settings');
      await axios.patch('/api/admin/commission-settings', commissionSettings);
      toast.show(t('userManagement.settingsUpdated') as string, 'success');
      await fetchCommissionSettings();
    } catch (e: any) {
      toast.show(String(e?.response?.data || t('userManagement.settingsError')), 'error');
    } finally {
      setBusy(null);
    }
  };

  const renderRoleBadge = (managedUser: ManagedUser) => {
    const palette = managedUser.role === 'ADMIN'
      ? 'bg-violet-100 text-violet-700'
      : managedUser.role === 'CLOSER'
        ? 'bg-indigo-100 text-indigo-700'
        : 'bg-sky-100 text-sky-700';

    return (
      <span className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${palette}`}>
        {t(`userManagement.roles.${managedUser.role}`)}
      </span>
    );
  };

  const renderCloserControls = (managedUser: ManagedUser) => {
    if (managedUser.role !== 'CLOSER') {
      return <span className="text-sm text-slate-500">{t('userManagement.notApplicable')}</span>;
    }

    return (
      <div className="space-y-2">
        <select
          value={managedUser.closerType || 'TEAM_MEMBER'}
          onChange={(e) => updateCloserType(managedUser, e.target.value as 'TEAM_MEMBER' | 'EXTERNAL')}
          disabled={busy === `${managedUser.id}:TYPE:TEAM_MEMBER` || busy === `${managedUser.id}:TYPE:EXTERNAL`}
          className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
        >
          <option value="TEAM_MEMBER">{t('closerType.TEAM_MEMBER')}</option>
          <option value="EXTERNAL">{t('closerType.EXTERNAL')}</option>
        </select>
        {managedUser.closerType === 'TEAM_MEMBER' ? (
          <select
            value={managedUser.salesCompensationModel || 'FIXED_1200'}
            onChange={(e) => updateCompensationModel(managedUser, e.target.value as 'FIXED_1200' | 'FIXED_500')}
            disabled={busy === `${managedUser.id}:MODEL:FIXED_1200` || busy === `${managedUser.id}:MODEL:FIXED_500`}
            className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
          >
            <option value="FIXED_1200">{t('compensationModel.FIXED_1200')}</option>
            <option value="FIXED_500">{t('compensationModel.FIXED_500')}</option>
          </select>
        ) : (
          <div className="text-xs text-slate-600">{t('userManagement.compensationExternal')}</div>
        )}
      </div>
    );
  };

  const renderActions = (managedUser: ManagedUser) => {
    const actions: React.ReactNode[] = [];
    const isSelf = currentUser?.id === managedUser.id;

    if (managedUser.role !== 'ADMIN') {
      actions.push(
        managedUser.status === 'BLOCKED' ? (
          <button
            key="unblock"
            onClick={() => updateStatus(managedUser, 'ACTIVE')}
            disabled={busy === `${managedUser.id}:STATUS:ACTIVE`}
            className="px-3 py-1 bg-[#47ABDB] disabled:opacity-60 text-white rounded"
          >
            {busy === `${managedUser.id}:STATUS:ACTIVE` ? t('common.loading') : t('userManagement.unblock')}
          </button>
        ) : (
          <button
            key="block"
            onClick={() => updateStatus(managedUser, 'BLOCKED')}
            disabled={busy === `${managedUser.id}:STATUS:BLOCKED`}
            className="px-3 py-1 bg-gray-200 disabled:opacity-60 text-gray-700 rounded"
          >
            {busy === `${managedUser.id}:STATUS:BLOCKED` ? t('common.loading') : t('userManagement.block')}
          </button>
        ),
      );
    }

    if (managedUser.role !== 'ADMIN') {
      actions.push(
        <button
          key="make-admin"
          onClick={() => updateRole(managedUser, 'ADMIN')}
          disabled={busy === `${managedUser.id}:ROLE:ADMIN:NONE`}
          className="px-3 py-1 bg-violet-600 disabled:opacity-60 text-white rounded"
        >
          {busy === `${managedUser.id}:ROLE:ADMIN:NONE` ? t('common.loading') : t('userManagement.makeAdmin')}
        </button>,
      );
    }

    if (managedUser.role === 'PARTNER') {
      actions.push(
        <button
          key="make-team-closer"
          onClick={() => updateRole(managedUser, 'CLOSER', 'TEAM_MEMBER', 'FIXED_1200')}
          disabled={busy === `${managedUser.id}:ROLE:CLOSER:TEAM_MEMBER`}
          className="px-3 py-1 bg-amber-500 disabled:opacity-60 text-white rounded"
        >
          {busy === `${managedUser.id}:ROLE:CLOSER:TEAM_MEMBER` ? t('common.loading') : t('userManagement.makeTeamCloser')}
        </button>,
      );
      actions.push(
        <button
          key="make-external-closer"
          onClick={() => updateRole(managedUser, 'CLOSER', 'EXTERNAL', null)}
          disabled={busy === `${managedUser.id}:ROLE:CLOSER:EXTERNAL`}
          className="px-3 py-1 bg-orange-500 disabled:opacity-60 text-white rounded"
        >
          {busy === `${managedUser.id}:ROLE:CLOSER:EXTERNAL` ? t('common.loading') : t('userManagement.makeExternalCloser')}
        </button>,
      );
    }

    if (managedUser.role === 'CLOSER') {
      actions.push(
        <button
          key="reset"
          onClick={() => resetPassword(managedUser)}
          disabled={busy === `${managedUser.id}:RESET`}
          className="px-3 py-1 bg-slate-600 disabled:opacity-60 text-white rounded"
        >
          {busy === `${managedUser.id}:RESET` ? t('common.loading') : t('userManagement.resetPassword')}
        </button>,
      );
      actions.push(
        <button
          key="demote"
          onClick={() => updateRole(managedUser, 'PARTNER')}
          disabled={busy === `${managedUser.id}:ROLE:PARTNER:NONE`}
          className="px-3 py-1 bg-orange-500 disabled:opacity-60 text-white rounded"
        >
          {busy === `${managedUser.id}:ROLE:PARTNER:NONE` ? t('common.loading') : t('userManagement.demoteToPartner')}
        </button>,
      );
    }

    if (managedUser.role === 'ADMIN') {
      actions.push(
        <span key="admin-note" className="text-sm text-slate-500">
          {isSelf ? t('userManagement.currentAdmin') : t('userManagement.adminProtected')}
        </span>,
      );
    }

    return actions;
  };

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('userManagement.title')}</h1>
      <div className="bg-blue-50 text-blue-900 border border-blue-100 rounded-xl p-4 mb-6 text-sm">
        {t('userManagement.intro')}
      </div>

      {commissionSettings && (
        <form onSubmit={saveCommissionSettings} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
          <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
            <div className="text-sm font-semibold text-[#111D4E]">{t('userManagement.settingsTitle')}</div>
            <div className="text-xs text-gray-600">{t('userManagement.settingsSubtitle')}</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {COMMISSION_FIELDS.map(([field, label]) => (
              <div key={field}>
                <label className="block text-sm font-medium mb-1">{t(label)}</label>
                <input
                  type="number"
                  min="0"
                  step={field.toLowerCase().includes('percent') ? '0.0001' : '0.01'}
                  value={commissionSettings[field]}
                  onChange={(e) => setCommissionSettings((prev) => prev ? ({ ...prev, [field]: Number(e.target.value) }) : prev)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  required
                />
              </div>
            ))}
          </div>
          <div className="px-5 pb-5 flex justify-end">
            <button type="submit" disabled={busy === 'settings'} className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md font-semibold">
              {busy === 'settings' ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      )}

      <form onSubmit={createCloser} className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden mb-6">
        <div className="px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div className="text-sm font-semibold text-[#111D4E]">{t('userManagement.createTitle')}</div>
          <div className="text-xs text-gray-600">{t('userManagement.createSubtitle')}</div>
        </div>
        <div className="p-5 flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="flex-1 w-full">
            <label className="block text-sm font-medium mb-1">{t('auth.email')}</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full px-3 py-2 border border-slate-200 rounded-md" />
          </div>
          <div className="w-full md:w-56">
            <label className="block text-sm font-medium mb-1">{t('userManagement.closerType')}</label>
            <select value={closerType} onChange={(e) => setCloserType(e.target.value as 'TEAM_MEMBER' | 'EXTERNAL')} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
              <option value="TEAM_MEMBER">{t('closerType.TEAM_MEMBER')}</option>
              <option value="EXTERNAL">{t('closerType.EXTERNAL')}</option>
            </select>
          </div>
          {closerType === 'TEAM_MEMBER' && (
            <div className="w-full md:w-56">
              <label className="block text-sm font-medium mb-1">{t('userManagement.compensationModel')}</label>
              <select value={salesCompensationModel} onChange={(e) => setSalesCompensationModel(e.target.value as 'FIXED_1200' | 'FIXED_500')} className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white">
                <option value="FIXED_1200">{t('compensationModel.FIXED_1200')}</option>
                <option value="FIXED_500">{t('compensationModel.FIXED_500')}</option>
              </select>
            </div>
          )}
          <button type="submit" disabled={busy === 'create'} className="px-4 py-2 bg-[#47ABDB] disabled:opacity-60 text-white rounded-md font-semibold">
            {busy === 'create' ? t('common.loading') : t('userManagement.createButton')}
          </button>
        </div>
      </form>

      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('userManagement.listTitle')}</div>
            <div className="text-xs text-gray-600">{t('userManagement.listSubtitle')}</div>
          </div>
          <button onClick={refreshAll} className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]">
            {t('common.refresh')}
          </button>
        </div>

        <div className="space-y-3 p-4 md:hidden">
          {users.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">{t('userManagement.empty')}</div>
          ) : (
            users.map((managedUser) => (
              <MobileDataCard
                key={managedUser.id}
                title={managedUser.email}
                badge={<StatusBadge kind="user" status={managedUser.status} />}
                subtitle={renderRoleBadge(managedUser)}
                rows={[
                  {
                    label: t('userManagement.role'),
                    value: renderRoleBadge(managedUser),
                  },
                  {
                    label: t('userManagement.closerSettings'),
                    value: renderCloserControls(managedUser),
                  },
                ]}
                actions={<>{renderActions(managedUser)}</>}
              />
            ))
          )}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left table-auto text-[15px] md:text-base">
            <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
              <tr>
                <th className="px-5 py-3 font-semibold">{t('auth.email')}</th>
                <th className="px-5 py-3 font-semibold">{t('userManagement.role')}</th>
                <th className="px-5 py-3 font-semibold">{t('userManagement.closerSettings')}</th>
                <th className="px-5 py-3 font-semibold">{t('companies.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.leads.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-base text-gray-500">{t('userManagement.empty')}</td>
                </tr>
              ) : (
                users.map((managedUser, idx) => (
                  <tr key={managedUser.id} className={idx % 2 === 0 ? 'bg-white align-top' : 'bg-[#FBFCFE] align-top'}>
                    <td className="px-5 py-4">
                      <span className="font-medium text-[#111D4E]">{managedUser.email}</span>
                    </td>
                    <td className="px-5 py-4">{renderRoleBadge(managedUser)}</td>
                    <td className="px-5 py-4 min-w-[220px]">{renderCloserControls(managedUser)}</td>
                    <td className="px-5 py-4">
                      <StatusBadge kind="user" status={managedUser.status} />
                    </td>
                    <td className="px-5 py-4 space-x-2 space-y-2">
                      {renderActions(managedUser)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default UserManagement;
